import { useState, useMemo, useRef, useCallback } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { iconsApi, type IconItem } from '../api/icons'
import { buildIconPath } from '@/lib/imagePaths'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import {
    Popover,
    PopoverContent,
    PopoverTrigger,
} from '@/components/ui/popover'
import { Search, Upload, X, Check, Grid3X3, List, Pencil } from 'lucide-react'
import { useToast } from '@/hooks/use-toast'

interface IconPickerProps {
    value: string | null
    onChange: (value: string | null) => void
    disabled?: boolean
}

/** Image that hides itself when it fails to load */
function IconImg({ src, alt, className, onBroken }: { src: string; alt: string; className?: string; onBroken?: () => void }) {
    const [hidden, setHidden] = useState(false)
    if (hidden) return null
    return (
        <img
            src={src}
            alt={alt}
            className={className}
            onError={() => {
                setHidden(true)
                onBroken?.()
            }}
        />
    )
}

export function IconPicker({ value, onChange, disabled = false }: IconPickerProps) {
    const { t } = useTranslation()
    const { toast } = useToast()
    const queryClient = useQueryClient()
    const [open, setOpen] = useState(false)
    const [search, setSearch] = useState('')
    const fileInputRef = useRef<HTMLInputElement>(null)
    const [uploading, setUploading] = useState(false)
    const [brokenIds, setBrokenIds] = useState<Set<number>>(new Set())
    const [viewMode, setViewMode] = useState<'icons' | 'list'>('icons')

    const { data: iconsResponse } = useQuery({
        queryKey: ['icons'],
        queryFn: async () => {
            const res = await iconsApi.list()
            return res.data.data
        },
        staleTime: 5 * 60 * 1000,
    })

    const icons = iconsResponse ?? []

    // Filter out icons whose images failed to load
    const validIcons = useMemo(
        () => icons.filter((i) => !brokenIds.has(i.id)),
        [icons, brokenIds]
    )

    const filtered = useMemo(() => {
        if (!search.trim()) return validIcons
        const term = search.toLowerCase()
        return validIcons.filter(
            (icon) =>
                icon.filename?.toLowerCase().includes(term) ||
                icon.description?.toLowerCase().includes(term) ||
                icon.tags?.toLowerCase().includes(term)
        )
    }, [validIcons, search])

    const selectedIcon = useMemo(
        () => (value ? icons.find((i) => String(i.id) === value) : null),
        [icons, value]
    )

    const handleSelect = useCallback(
        (icon: IconItem) => {
            onChange(String(icon.id))
            setOpen(false)
            setSearch('')
        },
        [onChange]
    )

    const handleClear = useCallback(
        (e: React.MouseEvent) => {
            e.stopPropagation()
            onChange(null)
        },
        [onChange]
    )

    const markBroken = useCallback((id: number) => {
        setBrokenIds((prev) => {
            const next = new Set(prev)
            next.add(id)
            return next
        })
    }, [])

    const handleUpload = useCallback(
        async (e: React.ChangeEvent<HTMLInputElement>) => {
            const file = e.target.files?.[0]
            if (!file) return

            const allowed = ['image/png', 'image/jpeg', 'image/gif', 'image/svg+xml', 'image/webp']
            if (!allowed.includes(file.type)) {
                toast({
                    title: 'Invalid file type',
                    description: 'Please upload a PNG, JPG, GIF, SVG, or WebP file.',
                    variant: 'destructive',
                })
                return
            }

            setUploading(true)
            try {
                const newIcon = await iconsApi.upload(file)
                await queryClient.invalidateQueries({ queryKey: ['icons'] })
                onChange(String(newIcon.id))
                setOpen(false)
                setSearch('')
                toast({ title: 'Icon uploaded', description: `${newIcon.filename} added to library.` })
            } catch (err: any) {
                toast({
                    title: 'Upload failed',
                    description: err?.message || 'Failed to upload icon.',
                    variant: 'destructive',
                })
            } finally {
                setUploading(false)
                if (fileInputRef.current) fileInputRef.current.value = ''
            }
        },
        [onChange, queryClient, toast]
    )

    return (
        <Popover open={open} onOpenChange={setOpen}>
            <div className="flex items-center gap-2">
                {selectedIcon ? (
                    <div className="flex items-center gap-2">
                        <IconImg
                            src={buildIconPath(selectedIcon.filename)}
                            alt=""
                            className="h-8 w-8 object-contain shrink-0"
                        />
                        <button
                            type="button"
                            className="text-muted-foreground hover:text-foreground"
                            onClick={(e) => { e.stopPropagation(); onChange(null) }}
                            disabled={disabled}
                        >
                            <X className="h-4 w-4" />
                        </button>
                    </div>
                ) : (
                    <span className="text-muted-foreground text-sm">No icon</span>
                )}
                <PopoverTrigger asChild>
                    <Button
                        variant="outline"
                        size="sm"
                        className="h-8 px-2"
                        disabled={disabled}
                    >
                        <Pencil className="h-3.5 w-3.5" />
                    </Button>
                </PopoverTrigger>
            </div>
            <PopoverContent className="w-[520px] p-0" align="start">
                <div className="p-2 border-b">
                    <div className="flex items-center gap-2">
                        <Search className="h-4 w-4 text-muted-foreground shrink-0" />
                        <Input
                            placeholder="Search icons..."
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            className="h-8 border-0 p-0 shadow-none focus-visible:ring-0"
                        />
                        <div className="flex shrink-0 border rounded-md">
                            <button
                                type="button"
                                className={`p-1.5 rounded-l-md transition-colors ${viewMode === 'icons' ? 'bg-accent text-foreground' : 'text-muted-foreground hover:text-foreground'}`}
                                onClick={() => setViewMode('icons')}
                                title="Grid view"
                            >
                                <Grid3X3 className="h-3.5 w-3.5" />
                            </button>
                            <button
                                type="button"
                                className={`p-1.5 rounded-r-md transition-colors ${viewMode === 'list' ? 'bg-accent text-foreground' : 'text-muted-foreground hover:text-foreground'}`}
                                onClick={() => setViewMode('list')}
                                title="List view"
                            >
                                <List className="h-3.5 w-3.5" />
                            </button>
                        </div>
                    </div>
                </div>
                <div
                    style={{ height: '400px', overflowY: 'scroll' }}
                    className="p-1"
                    onWheel={(e) => e.stopPropagation()}
                    onTouchMove={(e) => e.stopPropagation()}
                >
                    {filtered.length === 0 ? (
                        <div className="py-6 text-center text-sm text-muted-foreground">
                            No icons found.
                        </div>
                    ) : viewMode === 'icons' ? (
                        <div className="grid grid-cols-5 gap-2">
                            {filtered.map((icon) => {
                                const isSelected = value === String(icon.id)
                                return (
                                    <button
                                        key={icon.id}
                                        type="button"
                                        className={`relative flex flex-col items-center justify-center p-2 rounded-lg border hover:bg-accent cursor-pointer transition-colors ${
                                            isSelected ? 'bg-accent ring-2 ring-primary border-primary' : 'border-transparent'
                                        }`}
                                        onClick={() => handleSelect(icon)}
                                        title={icon.description || icon.filename}
                                    >
                                        <IconImg
                                            src={buildIconPath(icon.filename)}
                                            alt={icon.filename}
                                            className="h-14 w-14 object-contain"
                                            onBroken={() => markBroken(icon.id)}
                                        />
                                        <span className="text-[10px] text-muted-foreground mt-1 truncate w-full text-center">
                                            {(icon.description || icon.filename || '').replace(/^vsicon/, '').replace(/\.png$/, '')}
                                        </span>
                                        {isSelected && (
                                            <div className="absolute top-0 right-0 bg-primary rounded-full p-0.5">
                                                <Check className="h-2.5 w-2.5 text-primary-foreground" />
                                            </div>
                                        )}
                                    </button>
                                )
                            })}
                        </div>
                    ) : (
                        <div className="flex flex-col gap-0.5">
                            {filtered.map((icon) => {
                                const isSelected = value === String(icon.id)
                                return (
                                    <button
                                        key={icon.id}
                                        type="button"
                                        className={`flex items-center gap-3 px-2 py-1.5 rounded hover:bg-accent cursor-pointer transition-colors ${
                                            isSelected ? 'bg-accent ring-2 ring-primary' : ''
                                        }`}
                                        onClick={() => handleSelect(icon)}
                                    >
                                        <IconImg
                                            src={buildIconPath(icon.filename)}
                                            alt={icon.filename}
                                            className="h-8 w-8 object-contain shrink-0"
                                            onBroken={() => markBroken(icon.id)}
                                        />
                                        <span className="text-sm text-left truncate">
                                            {icon.description || icon.filename}
                                        </span>
                                        {isSelected && (
                                            <Check className="h-4 w-4 text-primary shrink-0 ml-auto" />
                                        )}
                                    </button>
                                )
                            })}
                        </div>
                    )}
                </div>
                <div className="border-t p-2 flex items-center justify-between">
                    <span className="text-xs text-muted-foreground">{filtered.length} icons</span>
                    <input
                        ref={fileInputRef}
                        type="file"
                        accept="image/png,image/jpeg,image/gif,image/svg+xml,image/webp"
                        className="hidden"
                        onChange={handleUpload}
                    />
                    <Button
                        variant="outline"
                        size="sm"
                        onClick={() => fileInputRef.current?.click()}
                        disabled={uploading}
                    >
                        <Upload className="h-4 w-4 mr-2" />
                        {uploading ? 'Uploading...' : 'Upload new icon'}
                    </Button>
                </div>
            </PopoverContent>
        </Popover>
    )
}
